/**
 * SEAL R&D Hub Dashboard
 * Phase 2, Step 2.4 — Main dashboard for SEAL observation data
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import SealStatusBadge from '../../components/seal/SealStatusBadge';
import adminApi from '../../services/adminApi';
import './SealDashboard.css';

// Try to import recharts (installed on deploy)
let LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis;
try {
  const recharts = require('recharts');
  LineChart = recharts.LineChart;
  Line = recharts.Line;
  XAxis = recharts.XAxis;
  YAxis = recharts.YAxis;
  CartesianGrid = recharts.CartesianGrid;
  Tooltip = recharts.Tooltip;
  ResponsiveContainer = recharts.ResponsiveContainer;
  AreaChart = recharts.AreaChart;
  Area = recharts.Area;
  RadarChart = recharts.RadarChart;
  Radar = recharts.Radar;
  PolarGrid = recharts.PolarGrid;
  PolarAngleAxis = recharts.PolarAngleAxis;
  PolarRadiusAxis = recharts.PolarRadiusAxis;
} catch (e) {
  // recharts not available — CSS fallback charts will be used
}

const PROCESSES = [
  { key: 'skillbot', label: 'SkillBot', icon: '🤖' },
  { key: 'profile_analysis', label: 'Profile Analysis', icon: '👤' },
  { key: 'roadmap_generation', label: 'Roadmap', icon: '🗺️' },
  { key: 'goals_analysis', label: 'Goals', icon: '🎯' },
  { key: 'guard_rails', label: 'Guard-Rails', icon: '🛡️' },
  { key: 'xconnect', label: 'xConnect', icon: '🔗' },
  { key: 'agent_orchestrator', label: 'Agent Orchestrator', icon: '🧭' },
  { key: 'agent_whatsapp', label: 'WhatsApp Agent', icon: '💬' },
  { key: 'agent_teams', label: 'Teams Agent', icon: '👥' }
];

const VERTICALS = [
  { key: 'wms', label: 'WMS' },
  { key: 'iseries', label: 'iSeries' },
  { key: 'civil', label: 'Civil' },
  { key: 'apparel', label: 'Apparel' },
  { key: 'skillcode', label: 'SkillCode' }
];

const SIGNAL_COLORS = {
  failure: '#ef4444',
  low_confidence: '#f97316',
  fallback_triggered: '#eab308',
  classifier_misroute: '#a855f7',
  high_latency: '#3b82f6',
  error: '#dc2626',
  drift_detected: '#6366f1',
  anomaly: '#ec4899',
  user_feedback: '#14b8a6'
};

const SealDashboard = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('system'); // 'system' | 'domain'
  const [overview, setOverview] = useState(null);
  const [verticalData, setVerticalData] = useState(null);
  const [selectedVertical, setSelectedVertical] = useState('wms');
  const [signals, setSignals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProcess, setSelectedProcess] = useState('all');
  const [refreshing, setRefreshing] = useState(false);


  const fetchOverview = useCallback(async () => {
    try {
      const res = await adminApi.get('/api/seal/dashboard/overview');
      if (res.data?.success) {
        setOverview(res.data.data);
      }
    } catch (err) {
      console.error('[SEAL Dashboard] Overview fetch error:', err);
      setError('Failed to load dashboard data');
    }
  }, []);

  const fetchVerticalData = useCallback(async (vertical) => {
    try {
      const res = await adminApi.get(`/api/seal/dashboard/vertical/${vertical}`);
      if (res.data?.success) {
        setVerticalData(res.data.data);
      }
    } catch (err) {
      console.error('[SEAL Dashboard] Vertical fetch error:', err);
    }
  }, []);

  const fetchSignals = useCallback(async () => {
    try {
      const res = await adminApi.get('/api/seal/observations/signals', { params: { limit: 20 } });
      if (res.data?.success) {
        setSignals(res.data.data.signals || []);
      }
    } catch (err) {
      console.error('[SEAL Dashboard] Signals fetch error:', err);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
  setRefreshing(true);
  await Promise.all([fetchOverview(), fetchSignals()]);
  if (view === 'domain') await fetchVerticalData(selectedVertical);
  setRefreshing(false);
}, [fetchOverview, fetchSignals, fetchVerticalData, view, selectedVertical]);


  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchOverview(), fetchSignals()]);
      setLoading(false);
    };
    loadAll();

    const interval = setInterval(() => {
      fetchOverview();
      fetchSignals();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchOverview, fetchSignals]);

  useEffect(() => {
    if (view === 'domain') {
      fetchVerticalData(selectedVertical);
    }
  }, [view, selectedVertical, fetchVerticalData]);

  const formatPercent = (val) => {
    if (val === null || val === undefined) return '—';
    return `${(val * 100).toFixed(1)}%`;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <AdminLayout showBackButton backPath="/admin/dashboard">
        <div className="seal-dashboard">
          <div className="seal-loading">
            <div className="spinner"></div>
            <p>Loading SEAL R&D Hub...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout showBackButton backPath="/admin/dashboard">
      <div className="seal-dashboard">
        {/* Top Bar */}
        <div className="seal-topbar">
          <div className="seal-topbar-left">
            <h1>
              <span className="header-icon">🔬</span>
              SEAL R&D Hub
            </h1>
            <SealStatusBadge />
            {overview?.seal_status?.mode && (
              <span className="seal-mode-badge">{overview.seal_status.mode}</span>
            )}
          </div>
          <div className="seal-topbar-right">
            <div className="seal-quick-stats">
              <div className="seal-quick-stat">
                <span className="seal-stat-value">{overview?.recent_activity?.observations_24h || 0}</span>
                <span className="seal-stat-label">Observations (24h)</span>
              </div>
              <div className="seal-quick-stat">
                <span className="seal-stat-value">{overview?.top_issues?.length || 0}</span>
                <span className="seal-stat-label">Active Issues</span>
              </div>
            </div>
            <div className="seal-nav-actions">
              <button className="btn btn-secondary" onClick={handleRefresh} disabled={refreshing}>
                {refreshing ? '⟳ Refreshing...' : '⟳ Refresh'}
              </button>
              
              <button className="btn btn-secondary" onClick={() => navigate('/admin/seal/config')}>
                Configuration
              </button>
              <button className="btn btn-secondary" onClick={() => navigate('/admin/seal/candidates')}>
                Candidates
              </button>
            </div>
          </div>
        </div>

        {/* View Toggle */}
        <div className="seal-view-toggle">
          <button
            className={`toggle-btn ${view === 'system' ? 'active' : ''}`}
            onClick={() => setView('system')}
          >
            System View
          </button>
          <button
            className={`toggle-btn ${view === 'domain' ? 'active' : ''}`}
            onClick={() => setView('domain')}
          >
            Domain View
          </button>
        </div>

        {error && (
          <div className="error-banner">
            <span>⚠️ {error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* ═══════════════ SYSTEM VIEW ═══════════════ */}
        {view === 'system' && (
          <div className="seal-system-view">
            {/* Platform Health Cards */}
            <div className="seal-section">
              <h2 className="seal-section-title">Platform Health</h2>
              <div className="seal-health-grid">
                {PROCESSES.map(proc => {
                  const health = overview?.platform_health?.[proc.key];
                  const firstVertical = health ? Object.keys(health)[0] : null;
                  const metrics = firstVertical ? health[firstVertical] : null;
                  const successRate = metrics?.success_rate;
                  const isHealthy = successRate !== undefined && successRate >= 0.9;
                  const issue = overview?.top_issues?.find(i => i.process === proc.key);

                  return (
                    <div key={proc.key} className={`seal-health-card ${isHealthy ? 'healthy' : metrics ? 'degraded' : 'no-data'}`}>
                      <div className="health-card-header">
                        <span className="health-icon">{proc.icon}</span>
                        <span className="health-name">{proc.label}</span>
                      </div>
                      <div className="health-rate">
                        <span className="health-rate-value">
                          {successRate !== undefined ? formatPercent(successRate) : '—'}
                        </span>
                        {metrics && (
                          <span className={`health-trend ${isHealthy ? 'up' : 'down'}`}>
                            {isHealthy ? '▲' : '▼'}
                          </span>
                        )}
                      </div>
                      <div className="health-obs">
                        {overview?.recent_activity?.observations_by_process?.[proc.key] || 0} obs (24h)
                      </div>
                      {issue && (
                        <div className="health-issue" title={issue.query_pattern}>
                          {issue.query_pattern?.substring(0, 60) || 'Issue detected'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Time-series Chart */}
            <div className="seal-section">
              <div className="seal-section-header">
                <h2 className="seal-section-title">Quality Trend (30 Days)</h2>
                <div className="seal-chart-filters">
                  <select
                    value={selectedProcess}
                    onChange={(e) => setSelectedProcess(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Processes</option>
                    {PROCESSES.map(p => (
                      <option key={p.key} value={p.key}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="seal-chart-container">
                {LineChart && overview ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={overview?.trend_data || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="quality_score" stroke="#4f46e5" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="seal-chart-placeholder">
                    <CSSBarChart
                      data={overview?.recent_activity?.observations_by_process || {}}
                      label="Observations by Process"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Fallback Rate Chart */}
            <div className="seal-section">
              <h2 className="seal-section-title">Observation Breakdown (24h)</h2>
              <div className="seal-chart-container">
                {AreaChart && overview ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={overview?.trend_data || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="fallback_rate" stroke="#f59e0b" fill="#fef3c7" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <CSSBarChart
                    data={overview?.recent_activity?.observations_by_type || {}}
                    label="Observations by Signal Type"
                    colorMap={SIGNAL_COLORS}
                  />
                )}
              </div>
            </div>

            {/* Recent Signals Feed */}
            <div className="seal-section">
              <h2 className="seal-section-title">Recent Signals</h2>
              <div className="seal-signals-table">
                {signals.length === 0 ? (
                  <div className="seal-empty">No observations recorded yet</div>
                ) : (
                  <table className="seal-table">
                    <thead>
                      <tr>
                        <th>Signal Type</th>
                        <th>Process</th>
                        <th>Vertical</th>
                        <th>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {signals.map((sig, i) => (
                        <tr key={sig._id || i}>
                          <td>
                            <span
                              className="signal-badge"
                              style={{ backgroundColor: SIGNAL_COLORS[sig.signal_type] || '#6b7280', color: '#fff' }}
                            >
                              {sig.signal_type?.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td>{sig.process}</td>
                          <td>{sig.vertical}</td>
                          <td className="text-muted">{formatTime(sig.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ═══════════════ DOMAIN VIEW ═══════════════ */}
        {view === 'domain' && (
          <div className="seal-domain-view">
            {/* Vertical Tabs */}
            <div className="seal-vertical-tabs">
              {VERTICALS.map(v => (
                <button
                  key={v.key}
                  className={`vertical-tab ${selectedVertical === v.key ? 'active' : ''}`}
                  onClick={() => setSelectedVertical(v.key)}
                >
                  {v.label}
                </button>
              ))}
            </div>

            {!verticalData ? (
              <div className="seal-loading"><div className="spinner"></div><p>Loading vertical data...</p></div>
            ) : (
              <>
                {/* Per-Vertical Health */}
                <div className="seal-section">
                  <h2 className="seal-section-title">{selectedVertical.toUpperCase()} — Health Overview</h2>
                  <div className="seal-metrics-row">
                    <div className="seal-metric-card">
                      <span className="metric-value">{formatPercent(verticalData.health_metrics?.success_rate)}</span>
                      <span className="metric-label">Success Rate</span>
                    </div>
                    <div className="seal-metric-card">
                      <span className="metric-value">{formatPercent(verticalData.health_metrics?.avg_confidence)}</span>
                      <span className="metric-label">Avg Confidence</span>
                    </div>
                    <div className="seal-metric-card">
                      <span className="metric-value">{formatPercent(verticalData.health_metrics?.fallback_rate)}</span>
                      <span className="metric-label">Fallback Rate</span>
                    </div>
                    <div className="seal-metric-card">
                      <span className="metric-value">{formatPercent(verticalData.health_metrics?.error_rate)}</span>
                      <span className="metric-label">Error Rate</span>
                    </div>
                    <div className="seal-metric-card">
                      <span className="metric-value">{verticalData.health_metrics?.total_observations_30d || 0}</span>
                      <span className="metric-label">Observations (30d)</span>
                    </div>
                  </div>
                </div>

                {/* Radar Chart — Process Health within Vertical */}
                <div className="seal-section">
                  <h2 className="seal-section-title">Process Health — {selectedVertical.toUpperCase()}</h2>
                  <div className="seal-chart-container">
                    {RadarChart && verticalData?.process_breakdown ? (
                      <ResponsiveContainer width="100%" height={350}>
                        <RadarChart data={Object.entries(verticalData.process_breakdown).map(([proc, m]) => ({
                          process: proc.replace(/_/g, ' '),
                          score: ((m.success_rate || 0) * 100).toFixed(0)
                        }))}>
                          <PolarGrid />
                          <PolarAngleAxis dataKey="process" tick={{ fontSize: 11 }} />
                          <PolarRadiusAxis domain={[0, 100]} />
                          <Radar dataKey="score" stroke="#4f46e5" fill="#4f46e5" fillOpacity={0.3} />
                        </RadarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="seal-process-bars">
                        {verticalData?.process_breakdown && Object.entries(verticalData.process_breakdown).map(([proc, m]) => (
                          <div key={proc} className="process-bar-row">
                            <span className="process-bar-label">{proc.replace(/_/g, ' ')}</span>
                            <div className="process-bar-track">
                              <div
                                className="process-bar-fill"
                                style={{ width: `${(m.success_rate || 0) * 100}%` }}
                              />
                            </div>
                            <span className="process-bar-value">{formatPercent(m.success_rate)}</span>
                          </div>
                        ))}
                        {(!verticalData?.process_breakdown || Object.keys(verticalData.process_breakdown).length === 0) && (
                          <div className="seal-empty">No process data for this vertical yet</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Top Failures */}
                <div className="seal-section">
                  <h2 className="seal-section-title">Top Failures — {selectedVertical.toUpperCase()}</h2>
                  {verticalData?.top_failures?.length > 0 ? (
                    <table className="seal-table">
                      <thead>
                        <tr>
                          <th>Query Pattern</th>
                          <th>Count</th>
                          <th>Process</th>
                          <th>Signal Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {verticalData.top_failures.map((f, i) => (
                          <tr key={i}>
                            <td className="text-mono">{f.query_pattern}</td>
                            <td><strong>{f.count}</strong></td>
                            <td>{f.process}</td>
                            <td>
                              <span className="signal-badge" style={{ backgroundColor: SIGNAL_COLORS[f.signal_type] || '#6b7280', color: '#fff' }}>
                                {f.signal_type}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="seal-empty">No failure patterns recorded yet</div>
                  )}
                </div>

                {/* Trend Data */}
                <div className="seal-section">
                  <h2 className="seal-section-title">30-Day Trend — {selectedVertical.toUpperCase()}</h2>
                  <div className="seal-chart-container">
                    {LineChart && verticalData?.trend_data?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={verticalData.trend_data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Line type="monotone" dataKey="observations" stroke="#4f46e5" strokeWidth={2} dot={false} name="Observations" />
                          <Line type="monotone" dataKey="failures" stroke="#ef4444" strokeWidth={2} dot={false} name="Failures" />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <CSSBarChart
                        data={(verticalData?.trend_data || []).reduce((acc, d) => {
                          if (d.date) acc[d.date.substring(5)] = d.observations || 0;
                          return acc;
                        }, {})}
                        label="Daily Observations"
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Prompt Bridge Stats */}
        {overview?.prompt_bridge_stats && (
          <div className="seal-section seal-bridge-stats">
            <h2 className="seal-section-title">Prompt Bridge Stats</h2>
            <div className="seal-metrics-row">
              <div className="seal-metric-card">
                <span className="metric-value">{overview.prompt_bridge_stats.hits || 0}</span>
                <span className="metric-label">Cache Hits</span>
              </div>
              <div className="seal-metric-card">
                <span className="metric-value">{overview.prompt_bridge_stats.misses || 0}</span>
                <span className="metric-label">Cache Misses</span>
              </div>
              <div className="seal-metric-card">
                <span className="metric-value">{overview.prompt_bridge_stats.cacheSize || 0}</span>
                <span className="metric-label">Cache Size</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

/**
 * CSS-based bar chart fallback (when recharts is not available)
 */
const CSSBarChart = ({ data, label, colorMap }) => {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <div className="seal-empty">No data available</div>;

  const maxVal = Math.max(...entries.map(([, v]) => v), 1);

  return (
    <div className="css-bar-chart">
      {label && <div className="css-chart-label">{label}</div>}
      <div className="css-bars">
        {entries.map(([key, val]) => (
          <div key={key} className="css-bar-item">
            <div className="css-bar-label" title={key}>{key.replace(/_/g, ' ')}</div>
            <div className="css-bar-track">
              <div
                className="css-bar-fill"
                style={{
                  width: `${(val / maxVal) * 100}%`,
                  backgroundColor: colorMap?.[key] || '#4f46e5'
                }}
              />
            </div>
            <div className="css-bar-value">{val}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SealDashboard;
